<template>
  <Loading 
    :loading="saveLoading"
    loading-text="Loading appointment duration..."
  >
    <div class="section-block">
      <div class="flex">
        <div class="min-w-[528px]">
          <div class="flex items-center">
            <img src="@/assets/my-calendar/availability/manage-history.svg" alt="duration" />
            <div class="card-title ml-2">Appointment Duration</div>
          </div>
          <div class="mt-1 text-[12px] text-[#999999]">
            Set the default length for appointments.
          </div>
        </div>
        <ResponsiveButton
          data-testid="my-calendar.appointment-duration-section.save-btn"
          button_type="filled"
          built_in_theme="dark"
          size="medium"
          display_name="Save"
          :disabled="isSaveDisabled"
          @click-button="handleSave"
        />
      </div>

      <div class="duration-field-row">
        <div class="duration-field-label">Default Appointment Duration</div>
        <div class="flex items-center gap-[7px]">
          <div class="w-[68px]">
            <SingleSelector
              v-model="localDuration"
              data-testid="my-calendar.appointment-duration-section.duration-select"
              class="duration-select"
              :options="durationOptions"
              size="medium"
              placeholder="Select"
            />
          </div>
          <div class="duration-unit-text">Minutes</div>
        </div>
      </div>
    </div>
  </Loading>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import Loading from '@/NewLibrary/Loading/Loading.vue';
import { durationOptions } from '@/views/my-calendar/mock-data';

const store = useStore();

const props = defineProps({
  duration: {
    type: [String, Number],
    default: 60,
  },
  soonestValue: {
    type: [String, Number],
    default: 24,
  },
  soonestUnit: {
    type: String,
    default: 'Hours',
  },
  furthestValue: {
    type: [String, Number],
    default: 30,
  },
  furthestUnit: {
    type: String,
    default: 'Days',
  },
});

const emit = defineEmits(['update:duration']);

const saveLoading = ref(false);
const localDurationState = ref('60');
const originalDurationValue = ref('60');

const localDuration = computed({
  get: () => localDurationState.value,
  set: value => {
    localDurationState.value = value;
  },
});

function normalizeRuleValue(value, fallback = '0') {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value);
}

function convertSoonestToMinutes(value, unit) {
  const normalizedValue = Number(value);
  if (!Number.isFinite(normalizedValue)) return 0;
  if (unit === 'Days') return normalizedValue * 24 * 60;
  return normalizedValue * 60;
}

function convertFurthestToDays(value, unit) {
  const normalizedValue = Number(value);
  if (!Number.isFinite(normalizedValue)) return 0;
  if (unit === 'Weeks') return normalizedValue * 7;
  if (unit === 'Months') return normalizedValue * 30;
  return normalizedValue;
}

const isSaveDisabled = computed(() => (
  saveLoading.value
  || !localDuration.value
  || normalizeRuleValue(localDuration.value, '60') === originalDurationValue.value
));

watch(
  () => normalizeRuleValue(props.duration, '60'),
  value => {
    localDurationState.value = value;
    originalDurationValue.value = value;
  },
  { immediate: true }
);

async function handleSave() {
  if (isSaveDisabled.value) return;

  saveLoading.value = true;
  try {
    await store.dispatch('calendar/updateUserBookingRules', {
      min_notice_minutes: convertSoonestToMinutes(props.soonestValue, props.soonestUnit),
      max_advance_days: convertFurthestToDays(props.furthestValue, props.furthestUnit),
      default_appointment_duration: Number(localDuration.value),
    });

    originalDurationValue.value = normalizeRuleValue(localDuration.value, '60');
    emit('update:duration', localDuration.value);
  } finally {
    saveLoading.value = false;
  }
}
</script>

<style scoped>
.section-block {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 24px;
}

.card-title {
  font-weight: 500;
  font-size: 18px;
  line-height: 100%;
  letter-spacing: 0%;
}

.duration-field-row {
  display: flex;
  align-items: center;
  gap: 20px;
}

.duration-field-label {
  color: #4A4A4A;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  min-width: 170px;
}



/* .duration-select :deep(.el-select__wrapper) {
  min-height: 40px;
  background: #F2F2F2;
  border: none;
  box-shadow: none !important;
  cursor: pointer;
  padding: 4px 8px;
} */

.duration-select :deep(.el-select__wrapper){
  min-height: 40px;
  --el-border-color: #C9C9C9;
  color: #666;
}

.duration-unit-text {
  margin-top: 2px;
  color: #666666;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.15px;
}
</style>
